﻿import { ref } from 'vue';
import { defineStore } from 'pinia';
import { apiFetch } from '@/services/api';

export interface SidebarConfigItem {
  id: string;
  hidden: boolean;
  order: number;
}

export const useSidebarConfigStore = defineStore('sidebarConfig', () => {
  const items = ref<SidebarConfigItem[]>([]);
  const loaded = ref(false);

  function nextFallbackOrder(): number {
    return items.value.length;
  }

  async function load() {
    try {
      const data = await apiFetch('/system/sidebar-config');
      items.value = Array.isArray(data?.items) ? data.items : [];
    } catch {
      items.value = [];
    } finally {
      loaded.value = true;
    }
  }

  async function save(newItems: SidebarConfigItem[]) {
    await apiFetch('/system/sidebar-config', {
      method: 'PUT',
      body: JSON.stringify({ items: newItems }),
    });
    items.value = newItems;
  }

  function isHidden(id: string): boolean {
    const found = items.value.find((i) => i.id === id);
    return found?.hidden ?? false;
  }

  function resolveHidden(id: string, defaultHidden = false): boolean {
    const found = items.value.find((i) => i.id === id);
    return found?.hidden ?? defaultHidden;
  }

  function getOrder(id: string, fallback: number): number {
    const found = items.value.find((i) => i.id === id);
    return found?.order ?? fallback;
  }

  async function setHidden(id: string, hidden: boolean, order = nextFallbackOrder()) {
    if (!loaded.value) await load();
    const existing = items.value.find((i) => i.id === id);
    const newItems = existing
      ? items.value.map((item) => (item.id === id ? { ...item, hidden, order: item.order ?? order } : item))
      : [...items.value, { id, hidden, order }];

    await save(newItems);
  }

  async function toggleHidden(id: string, defaultHidden = false) {
    await setHidden(id, !resolveHidden(id, defaultHidden));
  }

  /** Returns true only when the item is explicitly configured with hidden=false.
   *  Used for opt-in dashboard pages that are not shown in the main nav by default. */
  function isShownInNav(id: string): boolean {
    const found = items.value.find((i) => i.id === id);
    return found !== undefined && !found.hidden;
  }

  /** Toggle opt-in nav visibility for dashboard pages.
   *  Not in config / hidden -> pin (hidden:false). hidden:false -> unpin (hidden:true). */
  async function toggleNavPin(id: string) {
    if (!loaded.value) await load();
    const existing = items.value.find((i) => i.id === id);
    let newItems: SidebarConfigItem[];
    if (!existing || existing.hidden) {
      if (existing) {
        newItems = items.value.map((i) => (i.id === id ? { ...i, hidden: false } : i));
      } else {
        newItems = [...items.value, { id, hidden: false, order: items.value.length }];
      }
    } else {
      newItems = items.value.map((i) => (i.id === id ? { ...i, hidden: true } : i));
    }
    await save(newItems);
  }

  async function setNavPinned(id: string, pinned: boolean, order = nextFallbackOrder()) {
    await setHidden(id, !pinned, order);
  }

  return {
    items,
    loaded,
    load,
    save,
    isHidden,
    resolveHidden,
    getOrder,
    setHidden,
    toggleHidden,
    isShownInNav,
    toggleNavPin,
    setNavPinned,
  };
});
